import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FileManagerUpload } from "@shared/models/file-manager/file-manager-upload.model";
import { FileUploadStatus } from "@shared/constants/file-manager/file-upload-status";
import { FileProcessingStatus } from "@shared/constants/file-manager/file-processing-status";
import { FileManagerService } from "@services/file-manager/file-manager.service";
import { getReadableFileSize } from "@app/core/services/file-manager/utils/file-utils";

@Component({
  selector: 'app-uploaded-files-banner-manager',
  templateUrl: './uploaded-files-banner-manager.component.html',
  styleUrl: './uploaded-files-banner-manager.component.css'
})
export class UploadedFilesBannerManagerComponent {

  @Input() uploadedFiles: FileManagerUpload[] = [];
  @Output() onUploadingInProgress = new EventEmitter<boolean>();
  @Output() onFileProcessed = new EventEmitter<FileManagerUpload>();

  protected readonly uploadStatus = FileUploadStatus;
  readonly processingStatus = FileProcessingStatus;

  constructor(
    private fileManagerService: FileManagerService,
  ) {
  }
  get wrapperClasses(): { [key: string]: boolean } {
    return {
      'has-files': this.uploadedFiles.length > 0,
      'single-file': this.uploadedFiles.length === 1
    }
  };

  getFileStatus(data: FileManagerUpload): string {
    const isUploadInProgress = data.status === FileUploadStatus.IN_PROGRESS;
    if (isUploadInProgress) {
      return this.processingStatus.UPLOAD_IN_PROGRESS;
    }

    const isUploadError = data.status;

    if (isUploadError === this.uploadStatus.ERROR) {
      return this.processingStatus.UPLOAD_ERROR;
    }

    if (isUploadError === this.uploadStatus.FILE_SIZE_EXCEEDED) {
      return this.processingStatus.FILE_SIZE_EXCEEDED;
    }

    if (isUploadError === this.uploadStatus.PASSWORD_PROTECTED) {

      return this.uploadStatus.PASSWORD_PROTECTED;
    }

    const {
      error: errorIds,
      processed: processedIds,
    } = this.fileManagerService.fileStatusIds;

    const isProcessingError = errorIds.includes(data.id!);
    if (isProcessingError) {
      this.emitAndRemoveFile(data);
      return this.processingStatus.PROCESSING_ERROR;
    }

    const isProcessed = processedIds.includes(data.id!);
    if (isProcessed) {
      this.emitAndRemoveFile(data);
      return this.processingStatus.PROCESSED;
    }

    this.emitAndRemoveFile(data);
    return this.processingStatus.PROCESSING;
  };

  removeFile(data: FileManagerUpload): void {
    const index = this.uploadedFiles.findIndex((uploadedFile) => uploadedFile.file === data.file);

    if (data.status === this.uploadStatus.FILE_SIZE_EXCEEDED || data.status === this.uploadStatus.PASSWORD_PROTECTED || data.status === this.uploadStatus.IN_PROGRESS) {
      this.onUploadingInProgress.emit(false);
    }

    if (index >= 0) {
      this.fileManagerService.removeUploadedFile(data);
      if(this.isImageType(data)) {
        this.fileManagerService.removePreSelectedFile(data.id!);
        this.fileManagerService.removeSelectedFile(data.id!);
      }
      data.cancel!();
    }
  }

  isUploadProgressDisplayed(data: FileManagerUpload): boolean {
    const inProgress = data.status !== this.uploadStatus.ERROR && data.status !== this.uploadStatus.COMPLETED
      && data.status !== this.uploadStatus.FILE_SIZE_EXCEEDED && data.status !== this.uploadStatus.PASSWORD_PROTECTED;
    this.onUploadingInProgress.emit(inProgress);
    return inProgress;
  }

  pauseFileUpload(data: FileManagerUpload): void {
    data.status = FileUploadStatus.PAUSED;
    data.cancel!();
  };

  retryFileUpload(data: FileManagerUpload): void {
    data.status = FileUploadStatus.IN_PROGRESS;
    data.progress = 0;
    data.fetch!();
  };

  isStatusInProgress(status: string | undefined): boolean {
    return status === this.uploadStatus.IN_PROGRESS;
  }

  getFileStatusClass(status: string): string {
    if (this.isStatusErrorFileSizeExceeded(status)) {
      return 'file-size-exceeded-status-icon';
    }
    if (this.isStatusErrorPasswordProtected(status)) {
      return 'password-protected-status-icon';
    }
    if (status === this.uploadStatus.ERROR) {
      return 'upload-error-status-icon';
    }
    return 'processed-file-status-icon';
  }

  getMobileDisplayName(file: File): string {
    return this.fileManagerService.getParseFileNameToDisplayName(file.name, 25);
  }

  getDesktopDisplayName(file: File): string {
    return this.fileManagerService.getParseFileNameToDisplayName(file.name, 35);
  }

  getReadableFileSize(size: number): string {
    return getReadableFileSize(size);
  }


  isRetryButtonDisplayed(data: FileManagerUpload): boolean {
    if (data.status === this.uploadStatus.ERROR) {
      return true;
    }

    if (data.status === this.uploadStatus.PAUSED) {
      return true;
    }

    if (data.status === this.uploadStatus.FILE_SIZE_EXCEEDED) {
      return false;
    }

    return this.getFileStatus(data) === this.processingStatus.PROCESSING_ERROR;
  }

  emitAndRemoveFile(data: FileManagerUpload): void {
    this.onFileProcessed.emit(data);
    if(this.isNotImageType(data)) this.removeFile(data);
  }

  private isStatusErrorFileSizeExceeded(status: string): boolean {
    return status === this.uploadStatus.FILE_SIZE_EXCEEDED;
  }

  private isStatusErrorPasswordProtected(status: string): boolean {
    return status === this.uploadStatus.PASSWORD_PROTECTED;
  }

  private isNotImageType(data: FileManagerUpload): boolean {
    return !this.isImageType(data);
  }

  private isImageType(data: FileManagerUpload): boolean {
    const lowerCaseName = data.file.name.toLowerCase();
    return lowerCaseName.endsWith('.jpg') ||
      lowerCaseName.endsWith('.jpeg') ||
      lowerCaseName.endsWith('.png') ||
      lowerCaseName.endsWith('.webp');
  }

  shouldDisplayFileSize(file: FileManagerUpload): boolean {
    return this.isNotImageType(file)
  }
}
